import { Text, type TextProps, Tooltip } from '@mantine/core'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

export interface TruncatedTextProps extends TextProps {
  children: ReactNode
  /** Value shown in the tooltip; defaults to `children`. */
  label?: ReactNode
}

/**
 * Text that truncates with an ellipsis and reveals the full value in an
 * accessible tooltip (hover/touch) only when the content actually overflows.
 * The full value stays in the DOM as text content, so assistive tech reads it
 * regardless of the tooltip — the tooltip is a sighted-user affordance, not the
 * sole way the value is exposed.
 */
export function TruncatedText({ children, label, ...textProps }: TruncatedTextProps) {
  // Attached to the Tooltip (not the Text child): Mantine merges the Tooltip's
  // ref into the target element, whereas a ref on the child is clobbered by the
  // props spread in Tooltip's cloneElement under React 19.
  const ref = useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const measure = useCallback(() => {
    const el = ref.current
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth)
  }, [])

  useEffect(() => {
    measure()
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [measure, children])

  return (
    <Tooltip
      ref={ref}
      label={label ?? children}
      disabled={!isTruncated}
      events={{ hover: true, focus: false, touch: true }}
      multiline
      maw={360}
      withArrow>
      <Text truncate {...textProps}>
        {children}
      </Text>
    </Tooltip>
  )
}
